import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Time Off Report — CJ 2026-08-02, from the Square-transcribed report he
// liked the look of, rebuilt on live Monarch data.
//
// Read-only: a per-day absence chart for the next three weeks, then the
// full approved / needs-approval list by month. Approving still happens in
// Schedule > Time Off Approvals; nothing here writes.
//
// Palette is Monarch's own (MONARCH-DESIGN-SYSTEM.md) rather than the
// artifact's pine/clay — forest for calm, gold for building pressure,
// crimson for a day that needs attention.
//
// Sub-components are defined at module scope, never inside the render body
// — a component redefined each render remounts its subtree and drops focus.

const C = {
  ink: '#1c1f1d', inkSoft: '#4a4d4a', inkFaint: '#7a7d79',
  cream: '#f6f4ee', cream2: '#efe9dc', card: '#ffffff',
  border: '#d8d2c2', borderSoft: '#e6e0d0',
  forest: '#2f5d50', forestSoft: '#4f8377',
  gold: '#b48a47', goldDark: '#8e6c33',
  crimson: '#8a3a3a', crimsonBg: '#fdecec',
  successBg: '#e8f5e8', success: '#2e6a2e',
  infoBg: '#f3eef4',
};

const TIER_COLOR = { calm: C.forest, mid: C.gold, hot: C.crimson };

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 'YYYY-MM-DD' -> 'Aug 8'. Parsed at UTC noon so the label can't slip a day.
function shortDate(s) {
  if (!s) return '';
  const d = new Date(String(s).slice(0, 10) + 'T12:00:00Z');
  return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function dateRangeLabel(start, end) {
  if (!start) return '';
  if (start === end) return shortDate(start);
  const a = new Date(start + 'T12:00:00Z'), b = new Date(end + 'T12:00:00Z');
  // Same month reads better as "Aug 8–13" than "Aug 8 – Aug 13".
  if (a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear()) {
    return `${MONTH_ABBR[a.getUTCMonth()]} ${a.getUTCDate()}–${b.getUTCDate()}`;
  }
  return `${shortDate(start)} – ${shortDate(end)}`;
}

function Tag({ kind, children }) {
  const styles = {
    day:  { background: C.successBg, color: C.success },
    part: { background: '#f7e9e1',   color: '#8f4526' },
    pend: { background: '#eef1f6',   color: '#3f5170' },
    pto:  { background: C.infoBg,    color: '#5a3f70' },
  };
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '1px 7px',
      borderRadius: 999, letterSpacing: '.02em', whiteSpace: 'nowrap',
      ...(styles[kind] || styles.pend),
    }}>{children}</span>
  );
}

// Departures. CJ 2026-08-02: "this is a big event so we need to have it very
// prominent." A last day is not time off — the person does not come back —
// so it gets crimson, its own block above the chart, and a row treatment
// that cannot be mistaken for a holiday.
function DepartureBoard({ departures }) {
  if (!departures.length) return null;
  const soon = departures.filter((d) => d.withinChart).length;
  return (
    <div style={{
      border: `2px solid ${C.crimson}`, borderRadius: 12, background: C.crimsonBg,
      overflow: 'hidden', margin: '4px 0 18px',
    }}>
      <div style={{ padding: '11px 14px', borderBottom: `1px solid ${C.crimson}33`,
                    display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: C.crimson,
                       letterSpacing: '.01em' }}>
          Leaving — last days
        </span>
        <span style={{ fontSize: 12, color: C.ink }}>
          {departures.length} {departures.length === 1 ? 'person is' : 'people are'} leaving
          {soon > 0 && <> · <b style={{ color: C.crimson }}>{soon} inside the next three weeks</b></>}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13, minWidth: 520 }}>
          <tbody>
            {departures.map((d) => (
              <tr key={d.userId} style={{ background: d.withinChart ? '#f9dcdc' : 'transparent' }}>
                <td style={{ padding: '9px 14px', borderTop: `1px solid ${C.crimson}22`,
                             fontWeight: 800, color: C.ink, whiteSpace: 'nowrap' }}>
                  {d.name}
                  {d.role && <span style={{ fontWeight: 400, color: C.inkSoft, fontSize: 12 }}> · {d.role}</span>}
                </td>
                <td style={{ padding: '9px 14px', borderTop: `1px solid ${C.crimson}22`,
                             whiteSpace: 'nowrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px',
                                 borderRadius: 999, background: C.crimson, color: '#fff',
                                 letterSpacing: '.04em' }}>LAST DAY</span>
                  <b style={{ marginLeft: 8, fontVariantNumeric: 'tabular-nums' }}>
                    {shortDate(d.lastDay)}
                  </b>
                </td>
                <td style={{ padding: '9px 14px', borderTop: `1px solid ${C.crimson}22`,
                             whiteSpace: 'nowrap', color: C.inkSoft, fontSize: 12 }}>
                  {d.daysUntil === 0 ? 'today'
                    : d.daysUntil === 1 ? 'tomorrow'
                    : `in ${d.daysUntil} days`}
                </td>
                <td style={{ padding: '9px 14px', borderTop: `1px solid ${C.crimson}22`,
                             color: C.inkSoft }}>
                  {d.reason || <span style={{ color: C.inkFaint }}>no reason given</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatTile({ n, label, danger }) {
  // `danger` is only used by the departures tile: a number that means people
  // are leaving should not sit in the same calm forest green as "approved".
  const hot = danger && Number(n) > 0;
  return (
    <div style={{
      background: hot ? C.crimsonBg : C.card,
      border: `1px solid ${hot ? C.crimson : C.border}`, borderRadius: 12,
      padding: '12px 14px',
    }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: hot ? C.crimson : C.forest,
                    lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{n}</div>
      <div style={{ fontSize: 10, color: hot ? C.crimson : C.inkSoft, marginTop: 5,
                    fontWeight: hot ? 700 : 400,
                    textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
    </div>
  );
}

// The chart. One column per day; height is proportional to the worst day in
// the window so the shape stays readable whether the peak is 3 or 12.
function AbsenceChart({ days, maxCount, thresholds, scopeLabel, departures = [] }) {
  const scale = Math.max(maxCount, 1);
  // date -> names leaving that day, so a last day shows up in the timeline and
  // not only in the block above it.
  const leavingOn = new Map();
  for (const d of departures) {
    if (!leavingOn.has(d.lastDay)) leavingOn.set(d.lastDay, []);
    leavingOn.get(d.lastDay).push(d.name);
  }
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
                  padding: '16px 14px 10px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end',
                    height: 150, minWidth: Math.max(days.length * 26, 320) }}>
        {days.map((d) => (
          <div key={d.date} title={d.names.length ? `${d.date}: ${d.names.join(', ')}` : `${d.date}: nobody out`}
               style={{ flex: 1, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: C.ink, marginBottom: 2,
                           fontVariantNumeric: 'tabular-nums' }}>{d.count}</span>
            <div style={{
              width: '100%', minHeight: 3,
              height: `${Math.round((d.count / scale) * 100)}%`,
              background: TIER_COLOR[d.tier], borderRadius: '4px 4px 0 0',
            }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 4, marginTop: 5,
                    minWidth: Math.max(days.length * 26, 320) }}>
        {days.map((d) => {
          const leaving = leavingOn.get(d.date);
          return (
            <div key={d.date} title={leaving ? `Last day: ${leaving.join(', ')}` : undefined}
                 style={{ flex: 1, textAlign: 'center', fontSize: 9,
                          color: C.inkFaint, lineHeight: 1.25 }}>
              <span style={{ display: 'block', fontWeight: 700,
                             color: d.weekend ? C.crimson : C.ink }}>{d.dayNum}</span>
              {d.dow}
              {leaving && (
                <span style={{ display: 'block', marginTop: 2, fontSize: 8, fontWeight: 800,
                               color: '#fff', background: C.crimson, borderRadius: 3,
                               padding: '1px 0', letterSpacing: '.02em' }}>
                  LAST
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14,
                    fontSize: 11, color: C.inkSoft, alignItems: 'center' }}>
        {[
          ['calm', `≤${thresholds.mid - 1} out`],
          // At café scale mid and hot are often one apart, and "3–3 out"
          // reads like a bug. Collapse a single-value band to "3 out".
          ['mid', thresholds.hot - 1 === thresholds.mid
            ? `${thresholds.mid} out`
            : `${thresholds.mid}–${thresholds.hot - 1} out`],
          ['hot', `${thresholds.hot}+ out`],
        ].map(([tier, label]) => (
          <span key={tier} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <i style={{ width: 11, height: 11, borderRadius: 3, display: 'inline-block',
                        background: TIER_COLOR[tier] }} />{label}
          </span>
        ))}
        <span style={{ color: C.inkFaint }}>
          Approved full-day absences only · weekends in crimson · {scopeLabel}
        </span>
      </div>
    </div>
  );
}

function CrunchCards({ windows }) {
  if (!windows.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
                  gap: 12, marginTop: 14 }}>
      {windows.map((w) => (
        <div key={w.start} style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderLeft: `4px solid ${C.crimson}`, borderRadius: 10, padding: '12px 14px',
        }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em',
                        color: C.crimson, fontWeight: 700 }}>
            {dateRangeLabel(w.start, w.end)}
          </div>
          <div style={{ fontSize: 22, color: C.forest, fontWeight: 700, margin: '3px 0' }}>
            {w.peak}<span style={{ fontSize: 11, color: C.inkSoft, fontWeight: 400 }}> out at peak</span>
          </div>
          <div style={{ fontSize: 12, color: C.inkSoft }}>
            {w.names.slice(0, 6).join(', ')}{w.names.length > 6 ? ` +${w.names.length - 6} more` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

function RequestRow({ r, showStatus }) {
  return (
    <tr style={{ background: r.isVacation ? '#fbf6f0' : 'transparent' }}>
      <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.borderSoft}`,
                   fontWeight: 600, color: C.ink, whiteSpace: 'nowrap' }}>
        {r.name}{r.isVacation && <span style={{ color: C.crimson, fontSize: 10 }}> ●</span>}
      </td>
      <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.borderSoft}`,
                   fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {dateRangeLabel(r.start, r.end)}
      </td>
      <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.borderSoft}`,
                   color: C.inkFaint, fontSize: 12, whiteSpace: 'nowrap' }}>{r.dowLabel}</td>
      <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.borderSoft}` }}>
        {r.allDay ? <Tag kind="day">All day</Tag> : <Tag kind="part">{r.timeLabel}</Tag>}
        {r.usePto && <> <Tag kind="pto">PTO</Tag></>}
      </td>
      <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.borderSoft}`,
                   color: C.inkSoft, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {r.allDay ? `${r.days}d` : '—'}
      </td>
      <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.borderSoft}`,
                   color: C.inkSoft }}>{r.reason || <span style={{ color: C.inkFaint }}>—</span>}</td>
      {showStatus && (
        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.borderSoft}` }}>
          <Tag kind="pend">Needs approval</Tag>
        </td>
      )}
    </tr>
  );
}

function MonthTable({ months, showStatus, emptyText }) {
  if (!months.length) {
    return <div style={{ padding: '16px 4px', color: C.inkFaint, fontSize: 13 }}>{emptyText}</div>;
  }
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 12,
                  background: C.card, marginTop: 6 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13, minWidth: 620 }}>
        <thead>
          <tr>
            {['Team member', 'Date(s)', 'Day', 'Type', 'Length', 'Reason',
              ...(showStatus ? ['Status'] : [])].map((h) => (
              <th key={h} style={{
                textAlign: 'left', fontSize: 10, letterSpacing: '.07em', textTransform: 'uppercase',
                color: C.inkSoft, fontWeight: 700, padding: '9px 10px',
                borderBottom: `1.5px solid ${C.border}`, background: C.card,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {months.map((m) => [
            <tr key={m.month}>
              <td colSpan={showStatus ? 7 : 6} style={{
                background: C.cream, padding: '7px 10px',
                borderBottom: `1px solid ${C.border}`, borderTop: `1px solid ${C.border}`,
              }}>
                <span style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase',
                               color: C.forest, fontWeight: 700 }}>{m.label}</span>
                <span style={{ float: 'right', fontSize: 11, color: C.inkSoft, fontWeight: 600 }}>
                  {m.requests.length} request{m.requests.length === 1 ? '' : 's'}
                </span>
              </td>
            </tr>,
            ...m.requests.map((r) => <RequestRow key={r.id} r={r} showStatus={showStatus} />),
          ])}
        </tbody>
      </table>
    </div>
  );
}

export function TimeOffReportView({ currentUser, location }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const locationId = location?.id ?? null;

  async function load() {
    setLoading(true); setError('');
    try {
      const qs = locationId == null ? '' : `?location_id=${encodeURIComponent(locationId)}`;
      const r = await api('GET', `/api/tor/report${qs}`, null,
        { 'x-user-id': String(currentUser.id) });
      setData(r);
    } catch (e) {
      setError(e?.message || 'Could not load the time off report');
    } finally { setLoading(false); }
  }

  // Fetch when opened, and re-fetch whenever the café changes — the whole
  // point of the scope is that switching location switches the report.
  // Deliberately not gated on `!data`: that would pin the panel to whichever
  // café happened to be selected the first time it was opened.
  useEffect(() => { if (open) load(); }, [open, locationId]);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', textAlign: 'left', padding: '14px 16px', background: 'transparent',
          border: 'none', cursor: 'pointer', fontFamily: 'inherit',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
        <span>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>Time Off Report</span>
          <span style={{ display: 'block', fontSize: 11, color: C.inkFaint, marginTop: 2 }}>
            {location?.name
              ? `${location.name} — staff whose primary café this is`
              : "What's on the books — next three weeks, then everything by month"}
          </span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* A departure is the one thing on this report worth seeing without
              opening the panel. */}
          {data && data.stats.departures > 0 && (
            <span style={{ background: C.crimson, color: '#fff', borderRadius: 999,
                           padding: '2px 9px', fontSize: 11, fontWeight: 800 }}>
              {data.stats.departures} leaving
            </span>
          )}
          {data && data.stats.pendingCount > 0 && (
            <span style={{ background: '#eef1f6', color: '#3f5170', borderRadius: 999,
                           padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>
              {data.stats.pendingCount} need{data.stats.pendingCount === 1 ? 's' : ''} approval
            </span>
          )}
          <span style={{ fontSize: 12, color: C.inkFaint }}>{open ? '▲' : '▼'}</span>
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 18px', background: C.cream }}>
          {loading && <div style={{ padding: '14px 0', fontSize: 13, color: C.inkFaint }}>
            Loading time off…</div>}
          {error && (
            <div style={{ padding: 12, background: C.crimsonBg, borderRadius: 8,
                          borderLeft: `4px solid ${C.crimson}`, fontSize: 13, color: C.ink }}>
              {error}
              <button onClick={load} className="btn-secondary" style={{ fontSize: 12, marginLeft: 10 }}>
                Retry
              </button>
            </div>
          )}

          {data && !loading && (
            <>
              <div style={{ display: 'grid', gap: 10, margin: '4px 0 16px',
                            gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))' }}>
                <StatTile n={data.stats.approvedForward} label="Approved, upcoming" />
                <StatTile n={data.stats.pendingCount} label="Need a decision" />
                <StatTile n={data.stats.peakDate ? shortDate(data.stats.peakDate) : '—'}
                          label={data.stats.peakCount ? `Peak — ${data.stats.peakCount} out` : 'No absences'} />
                <StatTile n={data.stats.vacations} label="Vacations 3d+" />
                <StatTile n={data.stats.departures || 0} label="Leaving" danger />
              </div>

              <DepartureBoard departures={data.departures || []} />

              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.forest, margin: '0 0 3px' }}>
                All-day absences per day
              </h3>
              <p style={{ fontSize: 12, color: C.inkSoft, margin: '0 0 10px', maxWidth: '64ch' }}>
                {shortDate(data.window.start)} → {shortDate(data.window.end)} ·{' '}
                {data.window.days} days. Approved full-day and multi-day time off only —
                appointments and partial-hour blocks are listed below but don't remove
                anyone for the day.
              </p>
              <AbsenceChart days={data.days} maxCount={data.maxCount}
                            thresholds={data.thresholds}
                            departures={data.departures || []}
                            scopeLabel={data.scope?.locationName
                              ? `${data.scope.locationName} only`
                              : 'all cafés combined'} />
              <CrunchCards windows={data.crunchWindows} />

              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.forest, margin: '24px 0 3px' }}>
                Needs approval
              </h3>
              <p style={{ fontSize: 12, color: C.inkSoft, margin: '0 0 6px', maxWidth: '64ch' }}>
                Submitted and still waiting on a manager. Approve or deny them in
                Schedule → Time Off Approvals; this report is read-only.
              </p>
              <MonthTable months={data.pendingByMonth} showStatus
                          emptyText="Nothing waiting — every request has been decided." />

              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.forest, margin: '24px 0 3px' }}>
                Approved, by month
              </h3>
              <p style={{ fontSize: 12, color: C.inkSoft, margin: '0 0 6px', maxWidth: '64ch' }}>
                Everything approved from today forward. A{' '}
                <span style={{ color: C.crimson, fontWeight: 700 }}>●</span> marks a
                multi-day absence of 3 days or more.
              </p>
              <MonthTable months={data.approvedByMonth}
                          emptyText="No approved time off on the books from today forward." />

              {data.scope?.unassignedPeople > 0 && (
                <div style={{ marginTop: 14, padding: '9px 12px', background: '#f7e9e1',
                              borderLeft: `4px solid ${C.gold}`, borderRadius: 8,
                              fontSize: 12, color: C.ink }}>
                  <b>{data.scope.unassignedPeople}</b>{' '}
                  {data.scope.unassignedPeople === 1 ? 'person has' : 'people have'} time off
                  booked but no primary café set, so {data.scope.unassignedPeople === 1 ? 'they are' : 'they are'}{' '}
                  not in this or any other location's report. Set a primary café on their
                  profile to bring {data.scope.unassignedPeople === 1 ? 'them' : 'them'} in.
                </div>
              )}

              <div style={{ marginTop: 16, paddingTop: 10, borderTop: `1px solid ${C.border}`,
                            fontSize: 11, color: C.inkFaint }}>
                Live from Monarch · {data.stats.approvedForward} approved ·{' '}
                {data.stats.pendingCount} pending ·{' '}
                {data.scope?.locationName
                  ? `staff whose primary café is ${data.scope.locationName}`
                  : 'company-wide across all cafés'}
                <button onClick={load} className="btn-secondary"
                        style={{ fontSize: 11, marginLeft: 10 }}>↻ Refresh</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
